import { Checkbox } from 'ui/checkbox';

import { getUserDisplayName } from 'helpers/string.helpers';

export interface FileUserRow {
  userId: string | number;
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  hasAccess?: boolean;
  [key: string]: unknown;
}

interface InfoArg {
  getValue: () => unknown;
  renderValue: () => React.ReactNode;
  row: { original: FileUserRow };
}

interface FileUsersTableColumn {
  id: string;
  accessorKey?: string;
  header: () => React.JSX.Element;
  cell: (info: InfoArg) => React.ReactNode;
  headerClassName?: string;
  className?: string;
  enableSorting?: boolean;
}

interface UserAccessChange {
  userId: string | undefined;
  hasAccess: boolean;
}

interface GetFileUsersTableSettingsArgs {
  onUserChange: (changedUser: UserAccessChange) => void;
  onSelectAllUsers: (change: UserAccessChange) => void;
  isAllUsersSelected: boolean;
}

const commonStyles = 'pl-4 border-r-2 border-white font-semibold';

export const getFileUsersTableSettings = ({
  onUserChange,
  onSelectAllUsers,
  isAllUsersSelected,
}: GetFileUsersTableSettingsArgs): FileUsersTableColumn[] =>
  [
    {
      id: 'button-cell',
      header: () => (
        <Checkbox
          id="selectAllFiles"
          label="SELECT ALL"
          value="allSelected"
          checked={isAllUsersSelected}
          onChange={(_event, isChecked) =>
            onSelectAllUsers({ userId: 'allSelected', hasAccess: isChecked })
          }
          size="small"
          data-testid="checkbox-selectAllFiles"
        />
      ),
      cell: ({
        row: {
          original: { firstName, hasAccess, lastName, userId },
        },
      }: InfoArg) => (
        <Checkbox
          value={String(userId)}
          checked={!!hasAccess}
          onChange={(_event, isChecked) =>
            onUserChange({ userId: String(userId), hasAccess: isChecked })
          }
          size="small"
          aria-label={`File access for ${getUserDisplayName({
            firstName,
            lastName,
            id: userId,
          })}`}
          data-testid="checkbox-userForFile"
        />
      ),
      headerClassName: `${commonStyles} rounded-tl-md rounded-bl-md`,
      className: 'pl-4 py-3 w-[16%]',
    },
    {
      id: 'name',
      accessorKey: 'firstName',
      header: () => <span>USER NAME</span>,
      cell: ({
        row: {
          original: { firstName, lastName },
        },
      }: InfoArg) => `${firstName} ${lastName}`,
      headerClassName: commonStyles,
      className: 'pl-4 py-3 w-[42%]',
      enableSorting: false,
    },
    {
      id: 'jobTitle',
      accessorKey: 'jobTitle',
      header: () => <span>JOB TITLE</span>,
      cell: (info: InfoArg) => info.renderValue(),
      headerClassName: 'h-12 rounded-tr-md rounded-br-md pl-4',
      className: 'pl-4 py-3 w-[42%]',
      enableSorting: false,
    },
  ].filter(Boolean) as FileUsersTableColumn[];
